import { useState, useEffect, useCallback } from "react";
import { api } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Wallet, Download, Banknote, Smartphone, CreditCard, Building, Receipt as ReceiptIcon } from "lucide-react";
import { toast } from "sonner";

const METHOD_ICON = {
  cash: Banknote,
  mpesa: Smartphone,
  credit_card: CreditCard,
  bank_transfer: Building,
  cheque: ReceiptIcon,
};

const METHOD_LABEL = {
  cash: "Cash",
  mpesa: "M-PESA",
  credit_card: "Credit card",
  bank_transfer: "Bank transfer",
  cheque: "Cheque",
};

export default function MyPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/my/payments");
      setPayments(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load payments");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const totalKes = payments.reduce((sum, p) => sum + (p.currency === "KES" ? Number(p.amount || 0) : 0), 0);

  return (
    <div className="space-y-6 fade-in" data-testid="my-payments-page">
      <div>
        <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>My payments</h1>
        <p className="text-[#525C58] mt-1">All payments recorded against your invoices.</p>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2 text-lg">
            <span className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-emerald-700" /> Payment history ({payments.length})
            </span>
            {totalKes > 0 && (
              <span className="text-sm font-medium text-emerald-700" data-testid="my-payments-total">
                Total paid: KES {totalKes.toLocaleString()}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <p className="py-10 text-center text-[#7A8A84]">Loading…</p> :
           payments.length === 0 ? <p className="py-10 text-center text-[#7A8A84]">No payments recorded yet.</p> : (
            <div className="overflow-x-auto">
              <Table data-testid="my-payments-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((p) => {
                    const Icon = METHOD_ICON[p.method] || ReceiptIcon;
                    return (
                      <TableRow key={p.id} data-testid={`my-payment-row-${p.id}`}>
                        <TableCell className="text-sm">{p.paid_at}</TableCell>
                        <TableCell className="font-mono text-sm">#{p.invoice_number || "—"}</TableCell>
                        <TableCell>
                          <Badge className="bg-blue-50 text-blue-800 border border-blue-200 text-xs flex items-center gap-1 w-fit">
                            <Icon className="h-3 w-3" />
                            {METHOD_LABEL[p.method] || p.method}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-[#525C58] font-mono">{p.reference || "—"}</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">{p.currency} {Number(p.amount).toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/housing/payments/${p.id}/receipt`, "_blank")}
                            className="text-[#1B4332]"
                            title="Download receipt PDF"
                            data-testid={`my-receipt-${p.id}`}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
